import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Sparkles } from 'lucide-react'

import type {
    KiotVietMappingInput,
    KiotVietProduct,
} from '@/lib/api/product-api'
import type { ComboboxOption } from '@/components/ui/combobox'
import { PageBackButton } from '@/components/common/PageBackButton'
import { productApi } from '@/lib/api/product-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { defineListFilters } from '@/components/common/filters/core'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Combobox } from '@/components/ui/combobox'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

type MappingTab = 'all' | 'unmapped' | 'mapped'

/** Ô tìm kiếm — phần duy nhất của màn này là "bộ lọc" theo đúng nghĩa. */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.kiotviet_mapping_search_label,
        placeholder: m.kiotviet_mapping_search_erp,
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/products/kiotviet-mapping',
)({
    /**
     * `tab` KHÔNG đi qua config bộ lọc: nó là trạng thái điều hướng chứ không
     * phải bộ lọc — hiện dưới dạng tab kèm số đếm từng nhóm, không phải một ô
     * chọn trong thanh lọc. Nên validateSearch gộp hai nguồn.
     */
    validateSearch: (search: Record<string, unknown>) => ({
        ...FILTERS.validateSearch(search),
        tab: (search.tab === 'all' || search.tab === 'mapped'
            ? search.tab
            : 'unmapped') as MappingTab, // mặc định: Chưa nối
    }),
    component: KiotVietMappingPage,
})

// Màn nối SP (live-edit): 3 tab lọc theo trạng thái nối; mỗi dòng chọn/đổi/gỡ 1 SP
// KiotViet. Chọn mã đang thuộc SP khác → tự gỡ ở SP đó (+ toast). Bấm Lưu chỉ gửi
// các dòng đã sửa (gồm cả dòng bị tự gỡ). SP chưa nối được điền sẵn gợi ý khớp cao.
function KiotVietMappingPage() {
    const urlSearch = Route.useSearch()
    const tab = urlSearch.tab
    const navigate = useNavigate({ from: Route.fullPath })
    // replace: đổi tab không nhồi thêm history (Back không phải bấm nhiều lần).
    // Giữ nguyên từ khoá tìm kiếm khi đổi tab.
    const setTab = (t: MappingTab) =>
        void navigate({
            search: (prev) => ({ ...prev, tab: t }),
            replace: true,
        })

    const { values, barProps } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                // `tab` không thuộc config nên phải giữ lại bằng tay.
                search: (prev) => ({ ...next, tab: prev.tab }),
                replace: opts?.replace ?? false,
            }),
    })

    const qc = useQueryClient()
    const query = useQuery({
        queryKey: ['products', 'kiotviet-mapping'],
        queryFn: async () => (await productApi.getKiotVietMapping()).data,
    })
    const data = query.data

    // original: trạng thái đã LƯU (nguồn sự thật để lọc tab + tính dòng đã sửa).
    const original = useMemo(() => {
        const o: Record<string, string> = {}
        for (const p of data?.erp_products ?? []) o[p.id] = p.kiotviet_sku || ''
        return o
    }, [data])

    const suggestionByProduct = useMemo(() => {
        const map: Record<string, string> = {}
        for (const s of data?.suggestions ?? [])
            map[s.product_id] = s.kiotviet_sku
        return map
    }, [data])

    const productById = useMemo(() => {
        const map = new Map<string, { name: string }>()
        for (const p of data?.erp_products ?? [])
            map.set(p.id, { name: p.name })
        return map
    }, [data])

    // selection: giá trị đang chọn từng dòng ('' = chưa/không nối). Seed = trạng thái
    // đã lưu + gợi ý cho dòng chưa nối (gợi ý coi như "đã sửa" chờ lưu).
    const [selection, setSelection] = useState<Record<string, string>>({})
    useEffect(() => {
        if (!data) return
        const sel: Record<string, string> = {}
        for (const p of data.erp_products) sel[p.id] = p.kiotviet_sku || ''
        for (const s of data.suggestions) {
            if (!sel[s.product_id]) sel[s.product_id] = s.kiotviet_sku
        }
        setSelection(sel)
    }, [data])

    const dirtyIds = useMemo(
        () =>
            Object.keys(selection).filter(
                (id) => selection[id] !== (original[id] ?? ''),
            ),
        [selection, original],
    )

    // Đổi 1 dòng. Nếu chọn mã đang thuộc dòng khác → gỡ ở dòng đó + toast (mỗi mã ↔
    // 1 SP). Tính victim trước setState để toast không bắn 2 lần (StrictMode).
    const handleChange = (productId: string, code: string) => {
        if (code) {
            const victim = Object.keys(selection).find(
                (id) => id !== productId && selection[id] === code,
            )
            if (victim) {
                toast.info(
                    m.kiotviet_mapping_stolen({
                        name: productById.get(victim)?.name ?? '',
                    }),
                )
            }
        }
        setSelection((prev) => {
            const next = { ...prev }
            if (code) {
                for (const id of Object.keys(next)) {
                    if (id !== productId && next[id] === code) next[id] = ''
                }
            }
            next[productId] = code
            return next
        })
    }

    const mutation = useMutation({
        mutationFn: (mappings: KiotVietMappingInput[]) =>
            productApi.saveKiotVietMappings(mappings),
        onSuccess: (res) => {
            const failed = res.data?.failed.length ?? 0
            const saved = res.data?.saved ?? 0
            if (failed > 0) {
                toast.warning(
                    m.kiotviet_mapping_saved_partial({ saved, failed }),
                )
            } else {
                toast.success(m.kiotviet_mapping_saved_bulk({ count: saved }))
            }
            void qc.invalidateQueries({ queryKey: ['products'] })
        },
        onError: () => toast.error(m.kiotviet_mapping_save_failed()),
    })

    const handleSave = () => {
        if (dirtyIds.length === 0) {
            toast.info(m.kiotviet_mapping_no_changes())
            return
        }
        const mappings: KiotVietMappingInput[] = dirtyIds.map((id) => ({
            product_id: id,
            kiotviet_sku: selection[id], // '' = gỡ nối
        }))
        mutation.mutate(mappings)
    }

    const counts = useMemo(() => {
        const list = data?.erp_products ?? []
        const mapped = list.filter((p) => p.kiotviet_sku).length
        return { all: list.length, mapped, unmapped: list.length - mapped }
    }, [data])

    // Lọc theo tab bằng trạng thái ĐÃ LƯU (dòng không nhảy tab khi đang sửa dở).
    const rows = useMemo(() => {
        const list = data?.erp_products ?? []
        const byTab = list.filter((p) =>
            tab === 'all'
                ? true
                : tab === 'mapped'
                  ? !!p.kiotviet_sku
                  : !p.kiotviet_sku,
        )
        const q = values.q.trim().toLowerCase()
        if (!q) return byTab
        return byTab.filter(
            (p) =>
                p.name.toLowerCase().includes(q) ||
                p.sku.toLowerCase().includes(q),
        )
    }, [data, tab, values.q])

    // Options combobox dựng 1 lần từ TOÀN BỘ catalog, dùng chung mọi dòng.
    const kvOptions = useMemo(
        () => buildOptions(data?.kiotviet_products ?? []),
        [data],
    )
    // Mã có trong catalog đã tải — để biết dòng nào đang nối mã KHÔNG có trong
    // catalog (KiotViet tắt / SP KiotViet đã bị xoá) và vẫn hiện được mã đó.
    const kvCodes = useMemo(
        () => new Set((data?.kiotviet_products ?? []).map((p) => p.code)),
        [data],
    )

    const kvDisabled =
        !!data &&
        data.erp_products.length > 0 &&
        data.kiotviet_products.length === 0

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 page-pad">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div className="space-y-1">
                        <h1 className="text-xl font-semibold">
                            {m.kiotviet_mapping_title()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.kiotviet_mapping_desc()}
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleSave}
                    disabled={mutation.isPending || dirtyIds.length === 0}
                >
                    {m.kiotviet_mapping_save_changes({
                        count: dirtyIds.length,
                    })}
                </Button>
            </header>

            <div className="flex flex-wrap items-center gap-3">
                <Tabs
                    value={tab}
                    onValueChange={(v) => setTab(v as MappingTab)}
                >
                    <TabsList>
                        <TabsTrigger value="all">
                            {m.kiotviet_mapping_tab_all()} ({counts.all})
                        </TabsTrigger>
                        <TabsTrigger value="unmapped">
                            {m.kiotviet_mapping_tab_unmapped()} (
                            {counts.unmapped})
                        </TabsTrigger>
                        <TabsTrigger value="mapped">
                            {m.kiotviet_mapping_tab_mapped()} ({counts.mapped})
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="min-w-0 max-w-xs flex-1">
                    <ListFilterBar {...barProps} />
                </div>
            </div>

            {kvDisabled && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                    {m.kiotviet_mapping_kv_disabled()}
                </div>
            )}

            {/* Chỉ vùng bảng cuộn; header + toolbar đứng yên. */}
            <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
                {query.isLoading ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        {emptyText(tab)}
                    </div>
                ) : (
                    <Table>
                        <TableHeader className="sticky top-0 z-10 bg-background">
                            <TableRow>
                                <TableHead className="w-1/2">
                                    {m.kiotviet_mapping_col_erp()}
                                </TableHead>
                                <TableHead className="w-1/2">
                                    {m.kiotviet_mapping_col_kv()}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((p) => {
                                const value = selection[p.id] ?? ''
                                return (
                                    <MappingRow
                                        key={p.id}
                                        name={p.name}
                                        sku={p.sku}
                                        value={value}
                                        isDirty={
                                            value !== (original[p.id] ?? '')
                                        }
                                        isSuggested={
                                            !!value &&
                                            value === suggestionByProduct[p.id]
                                        }
                                        options={kvOptions}
                                        // Mã đang nối nhưng không có trong catalog → vẫn
                                        // hiện mã (không tụt về placeholder như chưa nối).
                                        selectedOption={
                                            value && !kvCodes.has(value)
                                                ? { value, label: value }
                                                : undefined
                                        }
                                        onChange={(code) =>
                                            handleChange(p.id, code)
                                        }
                                    />
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    )
}

function emptyText(tab: MappingTab): string {
    if (tab === 'mapped') return m.kiotviet_mapping_empty_mapped()
    if (tab === 'unmapped') return m.kiotviet_mapping_empty_unmapped()
    return m.kiotviet_mapping_empty_all()
}

// buildOptions: toàn bộ catalog KiotViet thành options, dựng 1 lần và dùng chung.
function buildOptions(kv: KiotVietProduct[]): ComboboxOption[] {
    return kv.map((p) => ({
        value: p.code,
        label: p.fullName || p.name,
        render: () => (
            <div className="flex w-full items-center gap-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{p.fullName || p.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                        {p.code}
                        {p.categoryName ? ` · ${p.categoryName}` : ''}
                    </p>
                </div>
                <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                    {formatVnd(p.basePrice)}
                </span>
            </div>
        ),
    }))
}

function MappingRow({
    name,
    sku,
    value,
    isDirty,
    isSuggested,
    options,
    selectedOption,
    onChange,
}: {
    name: string
    sku: string
    value: string
    isDirty: boolean
    isSuggested: boolean
    options: ComboboxOption[]
    selectedOption?: ComboboxOption
    onChange: (code: string) => void
}) {
    return (
        <TableRow className={isDirty ? 'bg-amber-50/60' : undefined}>
            <TableCell className="align-top">
                <div className="flex items-start gap-2">
                    {/* Chấm báo dòng có thay đổi chưa lưu. */}
                    <span
                        className={`mt-1.5 size-2 shrink-0 rounded-full ${
                            isDirty ? 'bg-amber-500' : 'bg-transparent'
                        }`}
                        aria-hidden
                    />
                    <div className="min-w-0">
                        <p className="font-medium">{name}</p>
                        <p className="text-xs text-muted-foreground">{sku}</p>
                    </div>
                </div>
            </TableCell>
            <TableCell className="align-top">
                <div className="flex items-center gap-2">
                    <Combobox
                        options={options}
                        value={value}
                        onChange={onChange}
                        selectedOption={selectedOption}
                        allowClear
                        placeholder={m.kiotviet_mapping_pick_placeholder()}
                        searchPlaceholder={m.kiotviet_mapping_search_kv()}
                        emptyText={m.kiotviet_mapping_no_kv()}
                    />
                    {isSuggested && (
                        <Badge
                            variant="secondary"
                            className="shrink-0 gap-1 whitespace-nowrap"
                        >
                            <Sparkles className="size-3" />
                            {m.kiotviet_mapping_suggest_badge()}
                        </Badge>
                    )}
                </div>
            </TableCell>
        </TableRow>
    )
}
